/**
 * Created by rathompson on 10/26/21
 * File: UserLayout
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  SubmitButton,
  Input,
} from '@panwds/react-form'
import {
  Bold,
  Button, LoadingIcon,
  ModalFooter,
  Banner,
  noop
} from '@panwds/react-ui'
import debouncePromise from 'awesome-debounce-promise'
import {
  BUTTON_CANCEL,
  BUTTON_NEXT,
  BUTTON_REMOVE,
  ERROR_INVALID_EMAIL, ERROR_USER_NOT_REGISTERED, ERROR_USER_NOT_PAN,
  LABEL_DISPLAY_NAME,
  LABEL_IDENTITY_ADDRESS,
  PLACEHOLDER_IDENTITY_ADDRESS,
  LABEL_INVITE_SENT
} from '../../../../../../constants/LabelConstants'
import {
  DATA_TEST_ADD_IDENTITY_ADDRESS,
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT,
} from '../../../../../../../common/constants/TestingDataIds'
import { validateEmail } from '../../../../../../../common/utils/Utils'
import { getVarValue } from '../../../../../../../common/utils/Sparky'
import { FF_PING_ID_ENABLED, FF_REQUIRE_PAN_IDENTITY, FF_ROLES_ENABLED } from '../../../../../../../common/utils/FeatureFlags'
import IAMService from '../../../../../../services/IAMService'
import Collapse from '../../../../../../../common/components/collapse/Collapse'
import SendInviteForm from './SendInviteForm'

const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)
const pingIdEnabled = getVarValue(FF_PING_ID_ENABLED, false)
const requirePANIdentity = getVarValue(FF_REQUIRE_PAN_IDENTITY, false)

/**
 *   Form fields specific to type user
 */
const UserLayout = ({ onBackPressed, onNextPressed, member, tenant, isEdit, onRemove, isLoading, setError, length }) => {
  /**
   *     ========= Local State ===========
   */
  const { clearErrors, setValue, formState, getValues, trigger } = useFormContext()
  const { isValid, errors } = formState
  const [validateLoading, setValidateLoading] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)

  const userNotRegistered = errors?.hasOwnProperty('identity_email') && errors?.identity_email?.type === 'isRegistered'
  const email = getValues('identity_email')

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    return () => {
      setValue('identity_email', '', { shouldDirty: true })
      clearErrors()
      setInviteSent(false)
    }
  }, [clearErrors, setValue])

  useEffect(() => {
    if (member) {
      setValue('identity_email', member?.identity_email)
      trigger()
    }
  }, [member, setValue, trigger])

  useEffect(() => {
    if (inviteSent) {
      trigger('identity_email')
      setShowSnackbar(true)
      setTimeout(() => setShowSnackbar(false), 3000)
    }
  }, [inviteSent, trigger])

  useEffect(() => {
    if (!userNotRegistered && email && email.endsWith('.iam.panserviceaccount.com')) {
      setInviteSent(false)
      setValidateLoading(false)
      clearErrors()
      trigger('identity_email')
    }
    if (email && email.endsWith('.iam.panserviceaccount.com')) {
      setInviteSent(false)
      setValidateLoading(false)
      clearErrors()
    }
  }, [email, clearErrors, trigger, userNotRegistered])

  const removeDisabled = useMemo(() => {
    if (rolesEnabled) {
      return isLoading || !member?.inherited_from?.includes(tenant?.id) || length <= 1
    }
    else {
      return isLoading || member?.inherited_from !== tenant?.id
    }
  }, [isLoading, member?.inherited_from, tenant?.id, length])

  return (
    <>
      <div>
        <div className='tw-flex tw-gap-2 tw-items-center'>
          <Bold appearance="semibold">{LABEL_IDENTITY_ADDRESS}</Bold>
          {validateLoading && <LoadingIcon/>}
        </div>
        <Input
          data-testid={DATA_TEST_ADD_IDENTITY_ADDRESS}
          label={LABEL_IDENTITY_ADDRESS}
          showLabel={false}
          register={{
            required: !member,
            validate: {
              isValidRegex: value => validateEmail(value) || ERROR_INVALID_EMAIL,
              isRegistered: debouncePromise(async value => {
                if (!pingIdEnabled || isEdit || inviteSent || (isValid && email === value) || value?.endsWith('.iam.panserviceaccount.com')) {
                  return true
                }
                let valid
                try {
                  setValidateLoading(true)
                  const { data } = await IAMService.ping.validate(tenant?.id, value)
                  valid = data?.profileExists || ERROR_USER_NOT_REGISTERED
                }
                catch (error) {
                  valid = error.message
                }
                setValidateLoading(false)
                return valid
              }, 200),
              isPAN: value => !requirePANIdentity || (requirePANIdentity && value.endsWith('paloaltonetworks.com')) || ERROR_USER_NOT_PAN,
            },
            onChange: async () => await trigger('identity_email')
          } }
          addClassName="tw-mt-1"
          name="identity_email"
          type="email"
          placeholder={PLACEHOLDER_IDENTITY_ADDRESS}
          required={!member}
          disabled={isEdit || inviteSent || isLoading}
        />
        {!isEdit && (
          <Collapse isOpen={(pingIdEnabled && userNotRegistered) || inviteSent} openClassName='tw-overflow-visible'>
            <SendInviteForm tsg_id={tenant?.id} {...{ setError, email, inviteSent, setInviteSent, validateLoading, isLoading }}/>
          </Collapse>
        )}
        {isEdit && (
          <>
            <Bold appearance="semibold">{LABEL_DISPLAY_NAME}:</Bold>
            <Input
              label={LABEL_DISPLAY_NAME}
              showLabel={false}
              register={{
                required: false,
                value: member?.display_name,
              }}
              addClassName="tw-mt-1"
              name="display_name"
              type="text"
              placeholder={PLACEHOLDER_IDENTITY_ADDRESS}
              required={false}
              disabled={true}
            />
          </>
        )}
        <ModalFooter style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '43px' }}>
          {isEdit && (
            <Button
              appearance="secondary-destructive"
              onClick={onRemove}
              disabled={removeDisabled}
            >{BUTTON_REMOVE}</Button>
          )}
          <Button
            data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
            onClick={onBackPressed}
            appearance="secondary"
          >{BUTTON_CANCEL}</Button>
          {(isEdit) ? (
            <Button
              appearance="primary"
              onClick={onNextPressed}
            >{BUTTON_NEXT}</Button>
          ) : (
            <SubmitButton
              data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
              appearance="primary"
              disabled={(validateLoading || isLoading || !isValid) || Boolean(Object.keys(formState.errors).length)}
            >{BUTTON_NEXT}</SubmitButton>
          )}
        </ModalFooter>
      </div>
      {showSnackbar && (
        <div className='tw-shadow-xl tw-self-center tw-mt-auto tw-ease-in-out tw-transition-all tw-duration-300' style={{ minWidth: '320px', marginLeft: '73px' }}>
          <Banner
            type="page"
            appearance="success"
            showIcon
          >{LABEL_INVITE_SENT}</Banner>
        </div>
      )}
    </>
  )
}

UserLayout.defaultProps = {
  onBackPressed: noop,
}
export default UserLayout
